import { Trans, useTranslation } from 'next-i18next';
import { ComponentProps, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Box, ButtonV2, InputV2, LoadingCircle } from '@bridebook/ui';
import { IconCross, IconQuestionMark, IconTickRound } from '@bridebook/ui/src/icons/dynamic';
import {
  DATE_FORMAT,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import { getDateFnsLocale } from '@bridebook/web/lib/i18n/utils/get-date-fns-format';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import {
  useRsvpForms,
  useRsvpData,
  useRsvpNavigation,
  useRsvpState,
  useRsvpAnalytics,
} from '../guest-rsvp.context';
import componentStyles from './guest-name-form.style';
import { PartyList } from './party-list';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils/get-theme-properties';
import SubmitButtonDesktopWrapper from '../partials/submit-button-desktop-wrapper';
import SubmitButtonMobileWrapper from '../partials/submit-button-mobile-wrapper';
import { useRouter } from 'next/router';

interface ISubmitButtonProps {
  isValid: boolean;
  styles: ReturnType<typeof componentStyles>;
  onClick: NonNullable<ComponentProps<typeof ButtonV2>['onClick']>;
  children: string;
}

const SubmitButton: React.FC<ISubmitButtonProps> = ({ isValid, styles, onClick, children }) => (
  <ButtonV2 styleWrapper={styles.submitButton(isValid)} onClick={onClick} disabled={!isValid}>
    {children}
  </ButtonV2>
);

interface IAfterInputProps {
  isLoading: boolean;
  guestNotFound: boolean;
  guestNoEvents: boolean;
  isValid: boolean;
}

const AfterInput: React.FC<IAfterInputProps> = ({
  isLoading,
  guestNotFound,
  guestNoEvents,
  isValid,
}) => {
  const icon = (() => {
    switch (true) {
      case isLoading:
        return <LoadingCircle size={24} color="indigoCrush" />;
      case guestNotFound:
        return <IconCross width={10} color="amber" />;
      case guestNoEvents:
        return <IconQuestionMark width={8} color="space75" />;
      case isValid:
        return <IconTickRound width={12} color="indigoCrush" />;
      default:
        return null;
    }
  })();

  return icon ? <Box pr={4}>{icon}</Box> : null;
};

const GuestNameForm = () => {
  const { nameForm } = useRsvpForms();
  const { setStep } = useRsvpNavigation();
  const { rsvpSubmissionStatus, isPreview } = useRsvpState();
  const {
    isLoading,
    weddingWebsite,
    weddingRsvp,
    guestNotFound,
    guestNoEvents,
    error,
    theme,
    relatedGuests,
  } = useRsvpData();
  const { trackGuestRsvpStarted } = useRsvpAnalytics();
  const { t } = useTranslation('weddingWebsite');

  const router = useRouter();
  const market = useMarket();

  const { guest: guestId } = router.query as {
    guest?: string;
  };

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);

  const styles = useMemo(
    () => componentStyles(themeProperties, isPreview),
    [themeProperties, isPreview],
  );

  const { control, handleSubmit } = nameForm;
  const {
    wedding: { partners },
  } = weddingWebsite;

  const handleGuestSwitch = useCallback(
    (member: { id: string; name: string }) => {
      nameForm.setValue('fullName', member.name);
      router.replace({ query: { ...router.query, guest: member.name } }, undefined, {
        shallow: true,
      });
    },
    [nameForm, router],
  );

  const onSubmit = useCallback(() => {
    if (rsvpSubmissionStatus === 'RESUBMISSION') {
      setStep('RSVP_FORM.SUMMARY');
    } else {
      setStep('RSVP_FORM.EVENTS');
    }
    trackGuestRsvpStarted();
  }, [setStep, rsvpSubmissionStatus, trackGuestRsvpStarted]);

  const isValid = useMemo(
    () =>
      !isLoading &&
      !error &&
      !guestNotFound &&
      !guestNoEvents &&
      !!weddingRsvp &&
      ['FIRST_SUBMISSION', 'RESUBMISSION'].includes(rsvpSubmissionStatus),
    [isLoading, error, guestNotFound, guestNoEvents, weddingRsvp, rsvpSubmissionStatus],
  );

  const showParty = useMemo(() => isValid && relatedGuests.length > 1, [isValid, relatedGuests]);

  const formattedDeadline = useMemo(() => {
    if (weddingRsvp?.deadline) {
      return format(new Date(weddingRsvp.deadline.seconds * 1000), DATE_FORMAT.DATE.SHORT, {
        locale: getDateFnsLocale(market.locale),
      });
    }
    return '';
  }, [weddingRsvp?.deadline, market.locale]);

  const onFormSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const helperText = useMemo(() => {
    if (guestNotFound) {
      return t(
        'rsvp.guest.name.helper.notFound',

        { partners },
      );
    }
    if (guestNoEvents) {
      return t(
        'rsvp.guest.name.helper.noEvents',

        { partners },
      );
    }
    return '';
  }, [guestNotFound, guestNoEvents, t, partners]);

  return (
    <>
      <Box style={styles.stepContentWrapper}>
        <Box as="h2" style={styles.title}>
          <Trans ns="weddingWebsite" i18nKey="rsvp.guest.name.title" values={{ partners }}>
            Welcome to <br />
            <Box as="span" style={styles.titleLarge}>
              {'{{partners.0}}'} and {'{{partners.1, possessive}}'} <br />
              wedding!
            </Box>
          </Trans>
        </Box>
        <Box as="h3" style={styles.subtitle(!!weddingRsvp?.deadline)}>
          {t('rsvp.guest.name.deadline', {
            deadline: formattedDeadline,
          })}
        </Box>
        <Box style={styles.formWrapper}>
          <Box style={styles.hint}>{t('rsvp.guest.name.hint')}</Box>
          <Box style={styles.fieldWrapper}>
            <Controller
              control={control}
              name="fullName"
              render={({
                field: { onChange, onBlur, value, name },
                fieldState: { error, isTouched },
              }) => (
                <InputV2
                  autoComplete="off"
                  label={t('rsvp.guest.name.label')}
                  placeholder={t('rsvp.guest.name.placeholder')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  name={name}
                  error={isTouched && error?.message}
                  readOnly={!!guestId}
                  afterInput={
                    <AfterInput
                      isLoading={isLoading}
                      guestNotFound={guestNotFound}
                      guestNoEvents={guestNoEvents}
                      isValid={isValid}
                    />
                  }
                  helper={helperText}
                />
              )}
            />
          </Box>
          {showParty && (
            <PartyList relatedGuests={relatedGuests} onGuestClick={handleGuestSwitch} />
          )}
        </Box>
        <SubmitButtonDesktopWrapper>
          <SubmitButton isValid={isValid} styles={styles} onClick={onFormSubmit}>
            {t('rsvp.guest.common.continue')}
          </SubmitButton>
        </SubmitButtonDesktopWrapper>
      </Box>
      <SubmitButtonMobileWrapper>
        <SubmitButton isValid={isValid} styles={styles} onClick={onFormSubmit}>
          {t('rsvp.guest.common.continue')}
        </SubmitButton>
      </SubmitButtonMobileWrapper>
    </>
  );
};

export default GuestNameForm;
